import type React from 'react';

import { Button, PetitionCard, Separator, Spinner, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import { useLazyLoadedListFocus } from '#src/app/shared/hooks/focus';

import { useTargetedPetitionList } from './hooks/useTargetedPetitionList';

export const TargetedPetitionList: React.FC<EmptyProps> = () => {
	const {
		data: { petitions, hasNextPage, nextPageLoading, sectionHeader, showDmrlSeparator },
		actions: { onClickPetitionLink, loadMore },
	} = useTargetedPetitionList();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const { translate, translatePlural } = useI18n();

	if (!petitions.length) return null;

	return (
		<div data-qa="dm-petitions-content" ref={containerRef} className="mb-10 md:mb-14">
			{showDmrlSeparator && <Separator orientation="horizontal" className="mb-14" data-testid="dmrl-separator" />}
			<Text as="h2" variant="heading" size="small" className="mb-5 md:mb-10">
				{sectionHeader}
			</Text>
			<div className="flex justify-start">
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{petitions.map((petition, index) => (
						<div key={petition.id}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TranslatePlural
										value="corgi.decision_maker_profile.petitions_list.fetched_more"
										count={petitions.length - lastEvent.previousItemsCount}
										replacements={{ count: petitions.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}

							<PetitionCard
								className="h-full"
								imageUrl={petition.photo?.petitionMedium.url}
								location={petition.relevantLocationLocalizedName || ''}
								petitionId={petition.id}
								petitionLinkOnClick={() => onClickPetitionLink(petition.id)}
								petitionLinkUrl={`/p/${encodeURIComponent(petition.slug)}`}
								signaturesLabel={translatePlural(
									'corgi.components.petition_card.signatures',
									petition.signatureState.signatureCount.displayed,
									{
										countLocalized: petition.signatureState.signatureCount.displayedLocalized,
									},
								)}
								mediaMentionsLabel={
									petition.mediaHitsConnection.totalCount > 0
										? translatePlural(
												'corgi.components.petition_card.media_mentions',
												petition.mediaHitsConnection.totalCount,
												{
													mediaMentionsCount: petition.mediaHitsConnection.totalCountLocalized,
												},
											)
										: undefined
								}
								title={petition.ask}
								ref={getListItemRef(index)}
							/>
						</div>
					))}
				</div>
			</div>

			{nextPageLoading && (
				<div className="mt-16">
					<div className="sr-only" role="alert">
						<Translate value="corgi.decision_maker_profile.petitions_list.fetching_more" />
					</div>
					<Spinner className="mx-auto" ref={loadingRef} data-testid="load-more-btn" />
				</div>
			)}

			{hasNextPage && !nextPageLoading && (
				<Button
					className="mt-5 basis-full md:mt-10 md:basis-1/3"
					size="small"
					onClick={() => {
						recordListEvent({ type: 'loadMore', itemsCount: petitions.length });
						loadMore();
					}}
					data-qa="dm-petitions-load-more"
					ref={loadMoreRef}
				>
					{translate('corgi.decision_maker_profile.load_more')}
				</Button>
			)}
		</div>
	);
};
